import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { getAbsenceReport, downloadReport, type AbsenceRow, type AbsenceReport } from '../api/reports.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { Alert } from '../components/Alert.js';

export const reportsAbsenceRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/reports/absence',
  component: ReportsAbsencePage,
});

const grp = (g: string | null): string => g ?? '(none)';

function ReportsAbsencePage(): JSX.Element {
  // Period range — a flow, not a snapshot (D2), so no single date picker.
  const [start, setStart] = useState('2026-01-01');
  const [end, setEnd] = useState('2026-03-31');
  const [dept, setDept] = useState('');
  const filters = { periodStart: start, periodEnd: end, ...(dept ? { department: dept } : {}) };
  const q = useQuery({
    queryKey: ['reports', 'absence', start, end, dept],
    queryFn: () => getAbsenceReport(filters),
    enabled: Boolean(start && end),
  });
  const [dlErr, setDlErr] = useState<unknown>(null);
  const download = async (format: 'csv' | 'pdf'): Promise<void> => {
    setDlErr(null);
    try { await downloadReport('absence', filters, format); } catch (e) { setDlErr(e); }
  };

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">Absence</h1>
        <p className="text-sm text-gray-400">Leave taken over a period, by type, department, and country. Measured in days.</p>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-900 p-4">
        <label className="text-sm text-gray-300 space-y-1"><span>From</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} aria-label="Period start" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>To</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} aria-label="Period end" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>Department <span className="text-gray-500">(optional)</span></span>
          <input value={dept} onChange={(e) => setDept(e.target.value)} aria-label="Department filter" placeholder="All" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={() => void download('csv')} disabled={!q.data} className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900 disabled:opacity-40">CSV (opens in Excel)</button>
          <button type="button" onClick={() => void download('pdf')} disabled={!q.data} className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900 disabled:opacity-40">PDF</button>
        </div>
      </div>

      {/* A 402 PLAN_REQUIRED renders the upgrade prompt → /billing (self-serve). */}
      <ApiErrorNotice error={q.error} fallback="Could not load the report." />
      <ApiErrorNotice error={dlErr} fallback="Download failed." />

      {q.isLoading ? <p className="text-sm text-gray-500">Loading…</p> : null}

      {q.data ? <AbsenceBody report={q.data} /> : null}
    </section>
  );
}

function AbsenceBody({ report }: { report: AbsenceReport }): JSX.Element {
  return (
    <div className="space-y-6">
      {/* Basis + filter notes MUST render — the number's scope can't live only in JSON (D8). */}
      <div className="space-y-1 text-xs text-gray-500">
        <p>{report.meta.basisNote}</p>
        {report.meta.filterNote ? <p className="text-amber">{report.meta.filterNote}</p> : null}
        <p>Total: {report.total.days} days across {report.total.requestCount} request{report.total.requestCount === 1 ? '' : 's'}</p>
      </div>
      {report.total.requestCount === 0 ? (
        <Alert variant="info" compact>No leave taken in this period.</Alert>
      ) : (
        <>
          <AxisTable title="By leave type" rows={report.byType} />
          <AxisTable title="By department" rows={report.byDepartment} />
          <AxisTable title="By country" rows={report.byCountry} />
        </>
      )}
    </div>
  );
}
function AxisTable({ title, rows }: { title: string; rows: AbsenceRow[] }): JSX.Element {
  if (rows.length === 0) return <></>;
  return (
    <div className="space-y-2">
      <h2 className="text-sm uppercase tracking-wide text-gray-500">{title}</h2>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-gray-500 text-xs">
          <th className="py-1 pr-4">Group</th><th className="py-1 pr-4 text-right">Days</th><th className="py-1 pr-4 text-right">Requests</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={grp(r.group)} className="border-t border-gray-900">
              <td className="py-2 pr-4 text-gray-200">{grp(r.group)}</td>
              <td className="py-2 pr-4 text-right font-mono text-gray-300">{r.days}</td>
              <td className="py-2 pr-4 text-right text-gray-500">{r.requestCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
